import {
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import TableRowsOutlined from "@mui/icons-material/TableRowsOutlined";
import ViewAgendaOutlined from "@mui/icons-material/ViewAgendaOutlined";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MOBILE_BREAKPOINT } from "@/styles/responsive";
import { type RequestListView } from "../hooks/useRequestListView";
import {
  ALPHA_TINT,
  focusRingSx,
  NO_TRANSITION,
  rowHoverBg,
  TEXT,
} from "../utils/tokens";
import { ReassistButton } from "./ReassistButton";
import { ReassistSearchField } from "./ReassistSearchField";

interface Props {
  /** 入力中の検索語（親の state）。 */
  draft: string;
  onDraftChange: (value: string) => void;
  onSearch: () => void;
  onClear: () => void;
  /** 検索が適用されているか。 */
  searching: boolean;
  view: RequestListView;
  onViewChange: (view: RequestListView) => void;
  /** 依頼の全件数。 */
  total: number;
  /** 検索で見つかった件数。 */
  matched: number;
}

const VIEWS: Array<{
  value: RequestListView;
  label: string;
  Icon: typeof ViewAgendaOutlined;
}> = [
  { value: "card", label: "カード", Icon: ViewAgendaOutlined },
  { value: "table", label: "テーブル", Icon: TableRowsOutlined },
];

const SEARCH_LABEL = "住所、マンション名、書類名、役所名で検索";

/** 部品の高さ（PC 40px、SP 44px）。検索欄、検索ボタン、表示切替で同じ値にする。 */
const CONTROL_HEIGHT = { xs: 44, [MOBILE_BREAKPOINT]: 40 };

/**
 * 依頼一覧のツールバー。検索・表示形式の切替・件数を、対応中と納品済みで共有する。
 * PC は1行（左=検索欄と検索ボタン、右=件数と切替）。SP は1段目に検索欄と検索ボタン、2段目に件数と切替。
 */
export const ReassistToolbar: React.FC<Props> = ({
  draft,
  onDraftChange,
  onSearch,
  onClear,
  searching,
  view,
  onViewChange,
  total,
  matched,
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();

  const count = (
    <Typography
      aria-live="polite"
      sx={{
        ...TEXT.meta,
        color: "text.secondary",
        whiteSpace: "nowrap",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {searching ? `${matched}件（全${total}件中）` : `全${total}件`}
    </Typography>
  );

  const toggle = (
    <ToggleButtonGroup
      exclusive
      value={view}
      aria-label="表示形式"
      onChange={(_, next: RequestListView | null) => {
        if (next !== null) onViewChange(next);
      }}
      sx={{
        flexShrink: 0,
        height: CONTROL_HEIGHT,
        "& .MuiToggleButton-root": {
          gap: 0.5,
          px: 1.5,
          py: 0,
          fontSize: 14,
          fontWeight: 400,
          lineHeight: 1.4,
          textTransform: "none",
          whiteSpace: "nowrap",
          color: "text.secondary",
          borderColor: theme.palette.border.main,
          transition: "background-color 0.2s",
          ...NO_TRANSITION,
          ...focusRingSx(theme),
          "&:hover": { backgroundColor: rowHoverBg(theme) },
          "&.Mui-selected, &.Mui-selected:hover": {
            color: theme.palette.dark.main,
            fontWeight: 700,
            backgroundColor: alpha(theme.palette.primary.main, ALPHA_TINT),
          },
        },
      }}
    >
      {VIEWS.map(({ value, label, Icon }) => (
        <ToggleButton key={value} value={value}>
          <Icon aria-hidden sx={{ fontSize: 18 }} />
          {label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );

  const search = (
    <Stack
      direction="row"
      gap={1}
      sx={{ alignItems: "center", flex: "1 1 auto", minWidth: 0 }}
    >
      <Box sx={{ flex: "1 1 auto", minWidth: 0, maxWidth: 480 }}>
        <ReassistSearchField
          value={draft}
          onChange={onDraftChange}
          label={SEARCH_LABEL}
          onEnter={onSearch}
          onClear={onClear}
        />
      </Box>
      <ReassistButton label="検索" variant="outlined" onClick={onSearch} />
    </Stack>
  );

  if (isMobile) {
    return (
      <Stack spacing={1.5}>
        {search}
        <Stack
          direction="row"
          gap={1}
          sx={{ alignItems: "center", justifyContent: "space-between" }}
        >
          {count}
          {toggle}
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      gap={3}
      sx={{ alignItems: "center", justifyContent: "space-between" }}
    >
      {search}
      <Stack direction="row" gap={2} sx={{ alignItems: "center" }}>
        {count}
        {toggle}
      </Stack>
    </Stack>
  );
};
